  import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
  import toast from "react-hot-toast";
  import {
    UPDATE_STAGE,
    GET_FILTER_ASSIGNEE,
    GET_FILTER_SPRINT,
    PROJECT_LIST,
    VIEW_ALL_ISSUES,
    VIEW_ALL_STASTUS,
  } from "services/endPoints";
  import { privateRequest } from "services/privateRequest";
  import { getProjectComponents } from "services/projectComponentService";
  import { severityOptions } from "utils/constants";
  import { DEFAULT_PROJECT_LIST_SORT } from "utils/projectListDefaults";
  import { getUserId } from "utils/utils";

  const typeList = [
    {
      label: "Epic",
      value: "EPIC",
    },
    {
      label: "Story",
      value: "STORY",
      isDefault: true,
    },
    {
      label: "Task",
      value: "TASK",
    },
    {
      label: "Bug",
      value: "BUG",
    },
  ];

  const priorityList = [
    {
      label: "Blocker",
      value: "BLOCKER",
    },
    {
      label: "Highest",
      value: "HIGHEST",
    },
    {
      label: "High",
      value: "HIGH",
    },
    {
      label: "Low",
      value: "LOW",
    },
    {
      label: "Lowest",
      value: "LOWEST",
    },
  ];

  const severityList = [
    { label: "Blocker", value: "BLOCKER" },
    { label: "Critical", value: "CRITICAL" },
    { label: "Major", value: "MAJOR" },
    { label: "Minor", value: "MINOR" },
  ];

  const filterInitialState = {
    list: [],
    status: "idle", // 'idle' | 'loading' | 'error'
    totalCount: 0,
  };

  const initialState = {
    issues: {
      list: [],
      status: "idle", // 'idle' | 'loading' | 'error'
      totalCount: 0,
      isExporting: false,
    },
    filters: {
      project: filterInitialState,
      status: filterInitialState,
      assignee: filterInitialState,
      reporter: filterInitialState,
      sprint: filterInitialState,
      component: filterInitialState,
      leadUser: filterInitialState,
      member: filterInitialState,
      type: {
        list: typeList,
      },
      priority: {
        list: priorityList,
      },
      severity: {
        list: severityOptions.map((option) => ({ label: option.optionLabel, value: option.value })),
      },
    },
  };

  const getParsedStages = (stages) => {
    let parasedStages = [];

    stages.forEach((stage) => {
      const newParsed = JSON.parse(stage);
      parasedStages = [...parasedStages, ...newParsed];
    });

    return parasedStages;
  };

  // Helper function to handle the export logic
  const downloadExportFile = (response) => {
    const contentDisposition = response.headers["content-disposition"];
    const fileName = contentDisposition
      ? contentDisposition.split("filename=")[1]
      : "issues.xlsx";
    const url = window.URL.createObjectURL(new Blob([response?.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
  };

  export const getAllIssues = createAsyncThunk(
    "viewAllIssue/getAllIssues",
    async function (
      {
        fields = [],
        pageNo,
        pageSize,
        projectId = [],
        releaseId,
        workType = [],
        issueTypes = [],
        priority = [],
        severity = [],
        reporters = [],
        assignee = [],
        sprint = [],
        stage = [],
        componentId = [],
        startDate = "",
        endDate = "",
        search = "",
        isExportIssue = false,
      },
      { rejectWithValue, signal },
    ) {
      const body = {
        ...(fields.length ? { fields } : {}),
        ...(projectId.length ? { projectId } : {}),
        ...(releaseId ? { releaseId } : {}),
        ...(workType.length ? { workType } : {}),
        ...(issueTypes.length ? { issueTypes } : {}),
        ...(priority.length ? { priority } : {}),
        ...(severity.length ? { severity } : {}),
        ...(reporters.length ? { reporters } : {}),
        ...(assignee.length ? { assignee } : {}),
        ...(sprint.length ? { sprint } : {}),
        ...(componentId.length ? { componentId } : {}),
        ...(stage.length ? { stage: getParsedStages(stage) } : {}), // TODO: check with backend is it added in backend side or nots
        ...(startDate ? { startDate } : {}),
        ...(endDate ? { endDate } : {}),
        ...(search ? { search } : {}),
        ...(pageNo ? { pageNo } : {}),
        ...(pageSize ? { pageSize } : {}),
        ...(isExportIssue ? { export: true } : { export: false }),
      };

      try {
        if (isExportIssue) {
          const exportResponse = await privateRequest.post(
            `${VIEW_ALL_ISSUES}?page-no=${pageNo}&page-size=${pageSize}&export=${isExportIssue}`,
            body,
            { signal, responseType: "blob" },
          );
          downloadExportFile(exportResponse);
          return;
        } else {
          const response = await privateRequest.post(
            `${VIEW_ALL_ISSUES}?page-no=${pageNo}&page-size=${pageSize}`,
            body,
            { signal },
          );
          return {
            data: response?.data?.data?.data || [],
            totalCount: response?.data?.data?.count?.total || 0,
          };
        }
      } catch (error) {
        if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED"){
          return;
        } 
        rejectWithValue(error);
        toast.error(error?.data?.message || "Some thing went wrong");
      }
    },
  );

  export const getFilterComponentOptions = createAsyncThunk(
    "viewAllIssue/getFilterComponentOptions",
    async function (
      { search = "", pageNo = 1, pageSize = 10 },
      { rejectWithValue, signal },
    ) {
      try {
        const response = await getProjectComponents({
          pageNo,
          pageSize,
          search,
          signal,
        });
        const inner = response?.data;
        const list = Array.isArray(inner?.data)
          ? inner.data
          : Array.isArray(inner)
            ? inner
            : [];
        const data =
          list
            .map((c) => {
              const id = c?.componentId ?? c?.id;
              if (id === undefined || id === null) return null;
              return { label: c?.name || String(id), value: id };
            })
            .filter(Boolean) || [];
        const totalCount =
          response?.data?.count?.total ??
          response?.data?.total ??
          response?.count?.total ??
          list.length;
        return { data, totalCount };
      } catch (error) {
        if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
          throw error;
        }
        rejectWithValue(error);
        toast.error(error?.data?.message || "Some thing went wrong");
      }
    },
  );

  export const importBulkIssue = createAsyncThunk(
    "viewAllIssue/importBulkIssue",
    async function ({ file }) {
      let formData = new FormData();
      formData.append("file", file);
      await privateRequest.post("api/import-issue", formData);
      toast.success("File imported Successfully");
    },
  );

  export const getFilterProjectsOptions = createAsyncThunk(
    "viewAllIssue/getFilterProjectsOptions",
    async function ({ search, pageNo, pageSize }, { rejectWithValue }) {
      try {
        const response = await privateRequest.get(PROJECT_LIST, {
          params: {
            search,
            pageNo,
            pageSize,
            ...DEFAULT_PROJECT_LIST_SORT,
          },
        });

        const data =
          response?.data?.data?.data?.map((option) => ({
            ...option,
            label: option.name,
            value: option.id,
          })) || [];

        return {
          data,
          totalCount: response?.data?.data?.count?.total || 0,
        };
      } catch (error) {
        rejectWithValue(error);
        toast.error(error?.data?.message || "Some thing went wrong");
      }
    },
  );

  export const getFilterStatusOptions = createAsyncThunk(
    "viewAllIssue/getFilterStatusOptions",
    async function (
      { projectId = [], search, pageNo, pageSize },
      { rejectWithValue },
    ) {
      try {
        let url = `${VIEW_ALL_STASTUS}?page-no=${pageNo}&page-size=${pageSize}`;

        if (projectId.length) {
          url = `${url}&project-id=${projectId}`;
        }

        if (search) {
          url = `${url}&search=${search}`;
        }

        const response = await privateRequest.get(url);

        const data =
          response?.data?.data?.data?.map((option) => ({
            ...option,
            label: option.stageName,
            value: option.ids,
          })) || [];

        return {
          data,
          totalCount: response?.data?.data?.count?.total || 0,
        };
      } catch (error) {
        rejectWithValue(error);
        toast.error(error?.data?.message || "Some thing went wrong");
      }
    },
  );

  export const getFilterAssigneeOptions = createAsyncThunk(
    "viewAllIssue/getFilterAssigneeOptions",
    async function (
      { projectId = [], search, pageNo, pageSize, type = "assignee" },
      { rejectWithValue },
    ) {
      try {
        let url = `${GET_FILTER_ASSIGNEE}?page-no=${pageNo}&page-size=${pageSize}`;

        if (projectId.length) {
          url = `${url}&project-id=${projectId}`;
        }

        if (search) {
          url = `${url}&search=${search}`;
        }

        const response = await privateRequest.get(url);

        const data =
          response?.data?.data?.data?.map((option) => ({
            ...option,
            label: option.username,
            value: option.userId,
          })) || [];
        //Add me and unassigned filter option when search query is empty for assignee
        if (!search) {
          data.unshift({ label: "Unassigned", value: -1 });
          data.unshift({ label: "Me", value: getUserId() || -1 });
        }
        return {
          data,
          totalCount: response?.data?.data?.count?.total || 0,
          type,
        };
      } catch (error) {
        rejectWithValue(error);
        toast.error(error?.data?.message || "Some thing went wrong");
      }
    },
  );

  export const getFilterSprintOptions = createAsyncThunk(
    "viewAllIssue/getFilterSprintOptions",
    async function (
      { projectId = [], search, pageNo, pageSize },
      { rejectWithValue },
    ) {
      try {
        let url = `${GET_FILTER_SPRINT}?page-no=${pageNo}&page-size=${pageSize}`;

        if (projectId.length) {
          url = `${url}&project-id=${projectId}`;
        }

        if (search) {
          url = `${url}&search=${search}`;
        }

        const response = await privateRequest.get(url);

        const data =
          response?.data?.data?.data?.map((option) => ({
            ...option,
            label: option.name,
            value: option.id,
          })) || [];
        // console.log(response.data)
        return {
          data,
          totalCount: response?.data?.data?.count?.total || 0,
        };
      } catch (error) {
        rejectWithValue(error);
        toast.error(error?.data?.message || "Some thing went wrong");
      }
    },
  );

  export const bulkChangeIssueStage = createAsyncThunk(
    "viewAllIssue/bulkChangeIssueStage",
    async ({ issueIds, stageId }, { rejectWithValue }) => {
      try {
        await privateRequest.patch(UPDATE_STAGE, {
          statusId: +stageId,
          issueIds,
        });
        toast.success("Stage updated successfully");
      } catch (error) {
        rejectWithValue(error);
        toast.error(error?.response?.data?.message || "Failed to update stage");
        throw error;
      }
    },
  );

  export const viewAllIssueSlcie = createSlice({
    name: "viewAllIssue",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
      builder.addCase(getAllIssues.pending, (state, action) => {
        if (action.meta.arg?.isExportIssue) {
          state.issues.isExporting = true;
          return;
        }

        state.issues.status = "loading";
        const pageNo = action.meta?.arg?.pageNo || 1;
        const shouldAppend = action.meta?.arg?.append && pageNo > 1;
        if (!shouldAppend) {
          state.issues.list = [];
        }
      });
      builder.addCase(getAllIssues.fulfilled, (state, action) => {
        if (!action.meta.arg?.isExportIssue) {
          const pageNo = action.meta?.arg?.pageNo || 1;
          const shouldAppend = action.meta?.arg?.append && pageNo > 1;
          const data = action.payload?.data || [];

          if (shouldAppend) {
            state.issues.list = [...state.issues.list, ...data];
          } else {
            state.issues.list = data;
          }

          state.issues.totalCount = action.payload?.totalCount;
        }
        state.issues.isExporting = false;
        state.issues.status = "idle";
      });
      builder.addCase(getAllIssues.rejected, (state, action) => {
        state.issues.status = "error";
        state.issues.isExporting = false;
      });
      builder.addCase(getFilterProjectsOptions.pending, (state) => {
        state.filters.project.status = "loading";
      });
      builder.addCase(getFilterProjectsOptions.fulfilled, (state, action) => {
        state.filters.project.status = "idle";
        state.filters.project.list = action.payload.data;
        state.filters.project.totalCount = action.payload.totalCount;
      });
      builder.addCase(getFilterProjectsOptions.rejected, (state, action) => {
        state.filters.project.status = "error";
      });
      builder.addCase(getFilterStatusOptions.pending, (state) => {
        state.filters.status.status = "loading";
      });
      builder.addCase(getFilterStatusOptions.fulfilled, (state, action) => {
        state.filters.status.status = "idle";
        state.filters.status.list = action.payload.data;
        state.filters.status.totalCount = action.payload.totalCount;
      });
      builder.addCase(getFilterStatusOptions.rejected, (state, action) => {
        state.filters.status.status = "error";
      });
      builder.addCase(getFilterAssigneeOptions.pending, (state, action) => {
        const type = action?.meta?.arg?.type || "assignee";
        if (type === "assignee") state.filters.assignee.status = "loading";
        else if (type === "leadUser") state.filters.leadUser.status = "loading";
        else if (type === "member") state.filters.member.status = "loading";
        else state.filters.reporter.status = "loading";
      });
      builder.addCase(getFilterAssigneeOptions.fulfilled, (state, action) => {
        const type = action.payload?.type;
        if (type === "assignee") {
          state.filters.assignee.status = "idle";
          state.filters.assignee.list = action.payload.data;
          state.filters.assignee.totalCount = action.payload.totalCount;
        } else if (type === "leadUser") {
          state.filters.leadUser.status = "idle";
          state.filters.leadUser.list = action.payload.data;
          state.filters.leadUser.totalCount = action.payload.totalCount;
        } else if (type === "member") {
          state.filters.member.status = "idle";
          state.filters.member.list = action.payload.data;
          state.filters.member.totalCount = action.payload.totalCount;
        } else {
          state.filters.reporter.status = "idle";
          state.filters.reporter.list = action.payload.data;
          state.filters.reporter.totalCount = action.payload.totalCount;
        }
      });
      builder.addCase(getFilterAssigneeOptions.rejected, (state, action) => {
        const type = action?.meta?.arg?.type || "assignee";
        if (type === "assignee") state.filters.assignee.status = "error";
        else if (type === "leadUser") state.filters.leadUser.status = "error";
        else if (type === "member") state.filters.member.status = "error";
        else state.filters.reporter.status = "error";
      });
      builder.addCase(getFilterSprintOptions.pending, (state, action) => {
          state.filters.sprint.status = "loading";
      });
      builder.addCase(getFilterSprintOptions.fulfilled, (state, action) => {
          state.filters.sprint.status = "idle";
          state.filters.sprint.list = action.payload.data;
          state.filters.sprint.totalCount = action.payload.totalCount;   
      });
      builder.addCase(getFilterSprintOptions.rejected, (state, action) => {
          state.filters.sprint.status = "error";
      });
      builder.addCase(getFilterComponentOptions.pending, (state) => {
        state.filters.component.status = "loading";
      });
      builder.addCase(getFilterComponentOptions.fulfilled, (state, action) => {
        state.filters.component.status = "idle";
        state.filters.component.list = action.payload.data;
        state.filters.component.totalCount = action.payload.totalCount;
      });
      builder.addCase(getFilterComponentOptions.rejected, (state) => {
        state.filters.component.status = "error";
      });
    },
  });

  export const { setIsLoading, setLandingPageData, setStatusForFilter } =
    viewAllIssueSlcie.actions;
  export default viewAllIssueSlcie.reducer;
